import type { NovelBackgroundId, NovelGame, NovelScene, NovelSilhouette } from './types';

const BACKGROUNDS = new Set<NovelBackgroundId>([
  'station_night',
  'school_night',
  'hospital_night',
  'hotel',
  'town',
  'isolated_house',
]);

const SILHOUETTES = new Set<NovelSilhouette>(['male_01', 'female_01']);

export class NovelValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NovelValidationError';
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** 生成 JSON を検証し、正規化した NovelGame を返す。不正なら throw。 */
export function validateNovelGame(raw: unknown): NovelGame {
  if (!isRecord(raw)) throw new NovelValidationError('ゲームデータが不正です');

  const title = String(raw.title ?? '').trim();
  const genre = String(raw.genre ?? '').trim();
  const tone = String(raw.tone ?? '').trim();
  const setting = String(raw.setting ?? '').trim();
  const startScene = String(raw.startScene ?? '').trim();
  if (!title || !genre || !tone || !setting || !startScene) {
    throw new NovelValidationError('必須フィールドが不足しています');
  }

  if (!Array.isArray(raw.characters) || raw.characters.length < 1) {
    throw new NovelValidationError('キャラクターがありません');
  }
  if (!Array.isArray(raw.scenes) || raw.scenes.length < 3) {
    throw new NovelValidationError('シーン数が不足しています');
  }

  const characters = raw.characters.map((c, i) => {
    if (!isRecord(c)) throw new NovelValidationError(`キャラクター ${i} が不正です`);
    const id = String(c.id ?? '').trim();
    const name = String(c.name ?? '').trim();
    const silhouette = String(c.silhouette ?? '') as NovelSilhouette;
    if (!id || !name || !SILHOUETTES.has(silhouette)) {
      throw new NovelValidationError(`キャラクター ${id || i} が不正です`);
    }
    return { id, name, silhouette };
  });

  const charIds = new Set(characters.map((c) => c.id));
  if (charIds.size !== characters.length) {
    throw new NovelValidationError('キャラクター id が重複しています');
  }

  const scenes: NovelScene[] = raw.scenes.map((s, i) => {
    if (!isRecord(s)) throw new NovelValidationError(`シーン ${i} が不正です`);
    const id = String(s.id ?? '').trim();
    const background = String(s.background ?? '') as NovelBackgroundId;
    const text = String(s.text ?? '').trim();
    if (!id || !text || !BACKGROUNDS.has(background)) {
      throw new NovelValidationError(`シーン ${id || i} が不正です`);
    }
    const charactersOnScene = Array.isArray(s.characters)
      ? s.characters.map((x) => String(x)).filter(Boolean)
      : [];
    for (const cid of charactersOnScene) {
      if (!charIds.has(cid)) throw new NovelValidationError(`未知のキャラクター: ${cid}`);
    }
    const choicesRaw = Array.isArray(s.choices) ? s.choices : [];
    const choices = choicesRaw.map((ch, j) => {
      if (!isRecord(ch)) throw new NovelValidationError(`選択肢 ${id}/${j} が不正です`);
      const choiceText = String(ch.text ?? '').trim();
      const nextScene = String(ch.nextScene ?? '').trim();
      if (!choiceText || !nextScene) throw new NovelValidationError(`選択肢 ${id}/${j} が不正です`);
      return { text: choiceText, nextScene };
    });
    const isEnding = Boolean(s.isEnding) || choices.length === 0;
    if (isEnding && choices.length > 0) {
      throw new NovelValidationError(`エンディングシーン ${id} に選択肢があります`);
    }
    return {
      id,
      background,
      characters: charactersOnScene,
      text,
      ...(isEnding ? { isEnding: true, choices: [] } : { choices }),
    };
  });

  const sceneIds = new Set(scenes.map((s) => s.id));
  if (sceneIds.size !== scenes.length) throw new NovelValidationError('シーン id が重複しています');
  if (!sceneIds.has(startScene)) throw new NovelValidationError('開始シーンがありません');

  for (const scene of scenes) {
    for (const ch of scene.choices ?? []) {
      if (!sceneIds.has(ch.nextScene)) {
        throw new NovelValidationError(`存在しないシーン参照: ${ch.nextScene}`);
      }
    }
  }

  const endings = scenes.filter((s) => s.isEnding || !(s.choices && s.choices.length));
  if (endings.length < 1) throw new NovelValidationError('エンディングがありません');

  // 到達可能性（開始から）
  const reachable = new Set<string>();
  const stack = [startScene];
  while (stack.length) {
    const id = stack.pop()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    const scene = scenes.find((s) => s.id === id);
    if (!scene) continue;
    for (const ch of scene.choices ?? []) stack.push(ch.nextScene);
  }
  if (reachable.size < 3) throw new NovelValidationError('分岐が不足しています');

  // 全到達シーンからエンディングへ到達できるか（有限深さ）
  const byId = new Map(scenes.map((s) => [s.id, s]));
  const canEnd = (id: string, visiting: Set<string>): boolean => {
    if (visiting.has(id)) return false;
    const scene = byId.get(id);
    if (!scene) return false;
    if (scene.isEnding || !(scene.choices && scene.choices.length)) return true;
    visiting.add(id);
    const ok = scene.choices.some((ch) => canEnd(ch.nextScene, visiting));
    visiting.delete(id);
    return ok;
  };
  for (const id of reachable) {
    if (!canEnd(id, new Set())) {
      throw new NovelValidationError(`エンディングに到達できないシーン: ${id}`);
    }
  }

  // 分岐があること
  const branchScenes = scenes.filter((s) => (s.choices?.length ?? 0) >= 2);
  if (branchScenes.length < 1) throw new NovelValidationError('選択肢による分岐がありません');

  return {
    title,
    genre,
    tone,
    setting,
    startScene,
    characters,
    scenes,
  };
}
